import { useEffect, useState, useRef } from "react";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError, UPLOADS_BASE } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Users, Store, Hash, RotateCcw, Tags, Percent, Plus, Trash2, Upload, Pencil, Wallet, Check, X as XIcon, Lock, LockKeyhole, ShieldCheck, ShieldAlert, Download, Loader2, Mail, Send, FlaskConical, GitBranch, FileBadge, Archive, History, Building2, FileSpreadsheet, Star, ChevronRight, ChevronLeft, Package, CornerDownRight, FolderInput, Smartphone, Monitor } from "lucide-react";
import { toast } from "sonner";
import UsersPage from "./UsersPage";
import ShopSettingsPage from "./ShopSettingsPage";
import PaTab from "./PaTab";
import DevicesTab from "./DevicesTab";
// BackupTab retiré côté site (demande explicite) : géré exclusivement
// depuis le panel d'hébergement désormais. Le composant lui-même reste
// défini dans son propre fichier ("./BackupTab") mais n'est plus importé
// ni monté ici.
import { useAuth } from "../../context/AuthContext";
import { CATEGORY_ICON_OPTIONS, CategoryIcon } from "../../lib/categoryIcons";

function NumberingTab() {
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState("");
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [previews, setPreviews] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    api.get("/shops").then((r) => {
      setShops(r.data);
      if (r.data.length > 0) setShopId((id) => id || r.data[0].id);
    });
  }, []);

  const load = async () => {
    if (!shopId) return;
    const { data } = await api.get("/admin/numbering", { params: { shop_id: shopId } });
    setRows(data);
    const d = {};
    data.forEach((r) => { d[r.doc_type] = { prefix: r.prefix, start_number: r.start_number, digits: r.digits, format: r.format }; });
    setDrafts(d);
    setPreviews({});
  };
  useEffect(() => { load(); }, [shopId]);

  const setField = (docType, field, value) => {
    setDrafts((d) => ({ ...d, [docType]: { ...d[docType], [field]: value } }));
  };

  const preview = async (docType) => {
    try {
      const { data } = await api.post(`/admin/numbering/${docType}/preview`, drafts[docType]);
      setPreviews((p) => ({ ...p, [docType]: data.exemple }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async (docType) => {
    setSavingId(docType);
    try {
      await api.put(`/admin/numbering/${docType}`, drafts[docType], { params: { shop_id: shopId } });
      toast.success("Numérotation mise à jour — le prochain document de cette boutique utilisera ces réglages");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Chaque boutique a sa propre numérotation, totalement indépendante des autres. Choisissez d'abord la boutique,
        puis, pour chaque type de document généré automatiquement, son préfixe, son numéro de départ, son nombre de
        chiffres et son format d'affichage. Le format peut utiliser <code className="px-1 rounded bg-secondary">{"{prefix}"}</code>,
        <code className="px-1 rounded bg-secondary ml-1">{"{year}"}</code> et <code className="px-1 rounded bg-secondary ml-1">{"{seq}"}</code> (obligatoire).
        Retirer <code className="px-1 rounded bg-secondary">{"{year}"}</code> rend la numérotation perpétuelle (jamais remise à zéro).
        Le changement s'applique uniquement au <strong>prochain</strong> document de <strong>cette boutique</strong> — ceux déjà émis
        ne sont jamais renumérotés, et les autres boutiques ne sont pas affectées.
        <br />
        <Lock size={12} className="inline mr-1 -mt-0.5" />
        La numérotation des <strong>tickets</strong>, <strong>factures</strong> et <strong>avoirs</strong> est verrouillée (numéro à
        6 chiffres, non modifiable) pour garantir la continuité de la séquence exigée par le registre fiscal. Leur numéro
        inclut désormais un <strong>code de boutique</strong> (3 lettres de la ville + 1 chiffre, ex. <code className="px-1 rounded bg-secondary">PAR1</code>)
        pour qu'un même numéro ne soit jamais affiché par deux boutiques différentes — y compris deux boutiques de la
        même ville, qui reçoivent chacune un chiffre différent. Ce code est <strong>définitivement figé</strong> dès la première
        attribution : il ne change plus jamais, même si la ville de la boutique est corrigée par la suite (Boutiques &amp; Dépôts) ;
        les tickets déjà émis ne sont jamais renumérotés.
      </p>

      <div className="max-w-xs space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Boutique</label>
        <Select value={shopId} onValueChange={setShopId}>
          <SelectTrigger data-testid="numbering-shop-select"><SelectValue placeholder="Choisir une boutique" /></SelectTrigger>
          <SelectContent>{shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {shopId && shops.find((s) => s.id === shopId)?.numbering_ville_code && (
        <p className="text-xs text-muted-foreground" data-testid="numbering-ville-code">
          Code de boutique utilisé dans les numéros de tickets/factures/avoirs :{" "}
          <span className="font-mono font-medium">{shops.find((s) => s.id === shopId).numbering_ville_code}</span>
        </p>
      )}

      <div className="space-y-3" data-testid="numbering-list">
        {rows.map((r) => {
          const draft = drafts[r.doc_type] || {};
          const locked = !!r.locked;
          return (
            <div key={r.doc_type} className={`rounded-xl border border-border bg-card p-4 ${locked ? "opacity-90" : ""}`} data-testid={`numbering-row-${r.doc_type}`}>
              <div className="flex items-center gap-2 mb-3">
                <Hash size={15} className="text-primary shrink-0" />
                <p className="font-heading font-semibold">{r.label}</p>
                {locked && (
                  <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground bg-secondary px-2 py-0.5 rounded-full ml-1" data-testid={`numbering-locked-${r.doc_type}`}>
                    <Lock size={11} /> Verrouillée
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Préfixe</label>
                  <Input disabled={locked} data-testid={`numbering-prefix-${r.doc_type}`} value={draft.prefix ?? ""} onChange={(e) => setField(r.doc_type, "prefix", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Numéro de départ</label>
                  <Input disabled={locked} data-testid={`numbering-start-${r.doc_type}`} type="number" min={1} value={draft.start_number ?? 1} onChange={(e) => setField(r.doc_type, "start_number", Number(e.target.value))} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Chiffres (zéros)</label>
                  <Input disabled={locked} data-testid={`numbering-digits-${r.doc_type}`} type="number" min={1} max={10} value={draft.digits ?? 4} onChange={(e) => setField(r.doc_type, "digits", Number(e.target.value))} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Format</label>
                  <Input disabled={locked} data-testid={`numbering-format-${r.doc_type}`} value={draft.format ?? ""} onChange={(e) => setField(r.doc_type, "format", e.target.value)} />
                </div>
              </div>
              {locked ? (
                <p className="text-xs text-muted-foreground mt-3">
                  Non modifiable — séquence continue à 6 chiffres imposée par le registre fiscal.
                </p>
              ) : (
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => preview(r.doc_type)} data-testid={`numbering-preview-${r.doc_type}`}>Aperçu</Button>
                    {previews[r.doc_type] && (
                      <span className="text-sm font-mono px-2 py-1 rounded bg-secondary" data-testid={`numbering-preview-value-${r.doc_type}`}>{previews[r.doc_type]}</span>
                    )}
                  </div>
                  <Button size="sm" onClick={() => save(r.doc_type)} disabled={savingId === r.doc_type} data-testid={`numbering-save-${r.doc_type}`} className="gap-1.5">
                    <RotateCcw size={13} /> Enregistrer
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftIcon, setDraftIcon] = useState("Package");
  const [draftFile, setDraftFile] = useState(null);
  const [draftParentId, setDraftParentId] = useState("");
  const [iconSearch, setIconSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [movingId, setMovingId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/categories");
    setCategories(data);
  };
  useEffect(() => { load(); }, []);

  // Catégories de premier niveau (candidates comme catégorie principale d'une
  // sous-catégorie) — une sous-catégorie ne pouvant jamais elle-même avoir de
  // sous-catégorie, ce sont les seules destinations valides pour "Déplacer".
  const categoriesPrincipales = categories.filter((c) => !c.parent_id);
  const sousCategories = (parentId) => categories.filter((c) => c.parent_id === parentId);

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setDraftName("");
    setDraftIcon("Package");
    setDraftFile(null);
    setDraftParentId("");
    setIconSearch("");
  };
  const startEdit = (c) => {
    setEditingId(c.id);
    setCreating(false);
    setDraftName(c.nom);
    setDraftIcon(c.icon_type === "lucide" ? c.icon_value : "Package");
    setDraftFile(null);
    setIconSearch("");
  };
  const cancel = () => { setCreating(false); setEditingId(null); setDraftFile(null); setIconSearch(""); };

  // Filtre le pack de 200 icônes par nom (recherche insensible à la casse et
  // aux accents) — une simple liste à cocher serait illisible à cette taille,
  // voir le champ de recherche affiché au-dessus de la grille d'icônes.
  const normalizeForSearch = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const filteredIconOptions = (() => {
    const q = normalizeForSearch(iconSearch.trim());
    if (!q) return CATEGORY_ICON_OPTIONS;
    return CATEGORY_ICON_OPTIONS.filter((opt) => normalizeForSearch(opt.label).includes(q) || normalizeForSearch(opt.value).includes(q));
  })();

  const save = async () => {
    const nom = draftName.trim();
    if (!nom) { toast.error("Le nom de la catégorie est obligatoire"); return; }
    setSaving(true);
    try {
      let cat;
      if (creating) {
        const { data } = await api.post("/categories", {
          nom, icon_type: "lucide", icon_value: draftIcon,
          parent_id: draftParentId || undefined,
        });
        cat = data;
      } else {
        const { data } = await api.patch(`/categories/${editingId}`, { nom, icon_type: draftFile ? undefined : "lucide", icon_value: draftFile ? undefined : draftIcon });
        cat = data;
      }
      if (draftFile) {
        const fd = new FormData();
        fd.append("file", draftFile);
        const { data: withIcon } = await api.post(`/categories/${cat.id}/icon`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        cat = withIcon;
      }
      toast.success("Catégorie enregistrée");
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette catégorie ? Les articles qui l'utilisent garderont son nom en texte libre.")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Catégorie supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deplacer = async (categoryId, parentId) => {
    try {
      await api.patch(`/categories/${categoryId}/move`, { parent_id: parentId || null });
      toast.success(parentId ? "Catégorie déplacée en sous-catégorie" : "Catégorie remontée au premier niveau");
      setMovingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const editorOpen = creating || editingId;

  const CategoryCard = ({ c, sousCategorie }) => (
    <div
      className={`rounded-xl border border-border bg-card p-3 flex flex-col items-center gap-2 text-center relative group ${sousCategorie ? "opacity-90" : ""}`}
      data-testid={`cat-row-${c.id}`}
    >
      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
        <CategoryIcon category={sousCategorie ? categories.find((p) => p.id === c.parent_id) : c} uploadsBase={UPLOADS_BASE} size={20} />
      </div>
      <p className="text-xs font-medium truncate w-full">{c.nom}</p>
      <div className="absolute top-1.5 right-1.5 hidden group-hover:flex gap-1">
        <button onClick={() => setMovingId(movingId === c.id ? null : c.id)} className="p-1 rounded bg-background/90 border border-border" title="Déplacer" data-testid={`cat-move-${c.id}`}><FolderInput size={11} /></button>
        <button onClick={() => startEdit(c)} className="p-1 rounded bg-background/90 border border-border" data-testid={`cat-edit-${c.id}`}><Pencil size={11} /></button>
        <button onClick={() => remove(c.id)} className="p-1 rounded bg-background/90 border border-border text-destructive" data-testid={`cat-delete-${c.id}`}><Trash2 size={11} /></button>
      </div>
      {movingId === c.id && (
        <div className="absolute z-10 top-full mt-1 left-0 right-0 rounded-lg border border-border bg-popover p-2 shadow-lg text-left" data-testid={`cat-move-panel-${c.id}`}>
          <p className="text-[10px] text-muted-foreground mb-1.5 px-1">Déplacer vers :</p>
          {!sousCategorie && (
            <button
              onClick={() => deplacer(c.id, null)}
              className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-secondary"
              data-testid={`cat-move-target-none-${c.id}`}
            >
              — Aucune (catégorie principale)
            </button>
          )}
          {sousCategorie && c.parent_id && (
            <button onClick={() => deplacer(c.id, null)} className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-secondary" data-testid={`cat-move-target-none-${c.id}`}>
              — Remonter au premier niveau
            </button>
          )}
          {categoriesPrincipales.filter((p) => p.id !== c.id && sousCategories(c.id).length === 0).map((p) => (
            <button
              key={p.id} onClick={() => deplacer(c.id, p.id)}
              disabled={c.parent_id === p.id}
              className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-secondary disabled:opacity-40 flex items-center gap-1.5"
              data-testid={`cat-move-target-${p.id}-${c.id}`}
            >
              <CategoryIcon category={p} uploadsBase={UPLOADS_BASE} size={12} /> Sous-catégorie de « {p.nom} »
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Catégories proposées dans le stock, avec leur icône. Utilisée comme visuel par défaut pour un article sans
          photo. Une catégorie peut être déplacée (icône <FolderInput size={11} className="inline" />) pour devenir
          sous-catégorie d'une autre (ex. « Magsafe » dans « Coques ») — l'icône affichée sur les articles reste
          toujours celle de la catégorie principale.
        </p>
        {!editorOpen && (
          <Button size="sm" onClick={startCreate} className="gap-1.5 shrink-0" data-testid="cat-add-button"><Plus size={14} /> Nouvelle catégorie</Button>
        )}
      </div>

      {editorOpen && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="cat-editor">
          <Input placeholder="Nom de la catégorie" value={draftName} onChange={(e) => setDraftName(e.target.value)} data-testid="cat-editor-name" autoFocus />
          {creating && (
            <div className="space-y-1">
              <p className="text-[11px] text-muted-foreground">Sous-catégorie de (optionnel) :</p>
              <Select value={draftParentId || "aucune"} onValueChange={(v) => setDraftParentId(v === "aucune" ? "" : v)}>
                <SelectTrigger data-testid="cat-editor-parent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aucune">Aucune (catégorie principale)</SelectItem>
                  {categoriesPrincipales.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <p className="text-[11px] text-muted-foreground">Icône — pack de {CATEGORY_ICON_OPTIONS.length} icônes pré-installées (ou téléversez votre propre image) :</p>
            <Input
              value={iconSearch}
              onChange={(e) => setIconSearch(e.target.value)}
              placeholder="Rechercher une icône (ex. « fleur », « voiture »...)"
              className="h-8 text-sm"
              data-testid="cat-editor-icon-search"
            />
            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto rounded-md border border-border/60 p-2" data-testid="cat-editor-icon-grid">
              {filteredIconOptions.length === 0 && (
                <p className="text-[11px] text-muted-foreground px-1 py-2">Aucune icône ne correspond à « {iconSearch} ».</p>
              )}
              {filteredIconOptions.map((opt) => (
                <button
                  key={opt.value}
                  title={opt.label}
                  onClick={() => { setDraftIcon(opt.value); setDraftFile(null); }}
                  className={`p-1.5 rounded-md border ${!draftFile && draftIcon === opt.value ? "border-primary bg-primary/10" : "border-border"}`}
                  data-testid={`cat-editor-icon-${opt.value}`}
                >
                  <opt.Icon size={16} />
                </button>
              ))}
            </div>
            <label className={`inline-flex p-1.5 rounded-md border cursor-pointer items-center gap-1 ${draftFile ? "border-primary bg-primary/10" : "border-border"}`}>
              <Upload size={16} />
              <span className="text-[11px]">Téléverser une image</span>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setDraftFile(e.target.files[0] || null)} data-testid="cat-editor-upload" />
            </label>
            {draftFile && <p className="text-[11px] text-muted-foreground">Image sélectionnée : {draftFile.name}</p>}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} data-testid="cat-editor-save">{saving ? "Enregistrement..." : "Enregistrer"}</Button>
            <Button size="sm" variant="outline" onClick={cancel}>Annuler</Button>
          </div>
        </div>
      )}

      <div className="space-y-5" data-testid="cat-list">
        {categoriesPrincipales.map((principale) => {
          const enfants = sousCategories(principale.id);
          return (
            <div key={principale.id}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                <CategoryCard c={principale} sousCategorie={false} />
              </div>
              {enfants.length > 0 && (
                <div className="ml-4 sm:ml-6 mt-2 pl-3 border-l-2 border-border">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {enfants.map((enfant) => (
                      <div key={enfant.id} className="flex items-start gap-1">
                        <CornerDownRight size={13} className="text-muted-foreground shrink-0 mt-3" />
                        <div className="flex-1"><CategoryCard c={enfant} sousCategorie /></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <TagsManager />
    </div>
  );
}

// Gestion des tags libres (Administrateurs > Catégories > Gérer les tags) :
// créer, renommer, supprimer un tag utilisable sur les fiches articles (voir
// TagPicker.jsx). Renommer/supprimer répercute automatiquement le
// changement sur tous les articles qui utilisaient déjà ce tag.
function TagsManager() {
  const [tags, setTags] = useState([]);
  const [nouveauNom, setNouveauNom] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/tags");
    setTags(data);
  };
  useEffect(() => { load(); }, []);

  const creer = async () => {
    const nom = nouveauNom.trim();
    if (!nom) { toast.error("Le nom du tag est obligatoire"); return; }
    setSaving(true);
    try {
      await api.post("/tags", { nom });
      toast.success("Tag créé");
      setNouveauNom("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const renommer = async (id) => {
    const nom = editValue.trim();
    if (!nom) { toast.error("Le nom du tag est obligatoire"); return; }
    try {
      await api.patch(`/tags/${id}`, { nom });
      toast.success("Tag renommé (répercuté sur les articles concernés)");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const supprimer = async (id) => {
    if (!window.confirm("Supprimer ce tag ? Il sera retiré de tous les articles qui l'utilisaient.")) return;
    try {
      await api.delete(`/tags/${id}`);
      toast.success("Tag supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="pt-6 border-t border-border space-y-3" data-testid="tags-manager">
      <div>
        <h3 className="font-heading font-semibold text-sm">Gérer les tags</h3>
        <p className="text-xs text-muted-foreground max-w-2xl">
          Tags libres utilisables sur les fiches articles (ex. « S26 Ultra »), en complément de la catégorie/sous-catégorie.
          Renommer ou supprimer un tag répercute automatiquement le changement sur tous les articles qui l'utilisent déjà.
        </p>
      </div>
      <div className="flex gap-2 max-w-sm">
        <Input placeholder="Nouveau tag..." value={nouveauNom} onChange={(e) => setNouveauNom(e.target.value)} onKeyDown={(e) => e.key === "Enter" && creer()} data-testid="tags-manager-new-input" />
        <Button size="sm" onClick={creer} disabled={saving} className="gap-1.5 shrink-0" data-testid="tags-manager-new-save"><Plus size={14} /> Créer</Button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="tags-manager-list">
        {tags.map((t) => (
          <div key={t.id} className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5" data-testid={`tags-manager-row-${t.id}`}>
            {editingId === t.id ? (
              <>
                <Input
                  autoFocus value={editValue} onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && renommer(t.id)}
                  className="h-6 text-xs w-28" data-testid={`tags-manager-edit-input-${t.id}`}
                />
                <button onClick={() => renommer(t.id)} title="Valider" data-testid={`tags-manager-edit-save-${t.id}`}><Check size={13} className="text-emerald-600" /></button>
                <button onClick={() => setEditingId(null)} title="Annuler"><XIcon size={13} className="text-muted-foreground" /></button>
              </>
            ) : (
              <>
                <span className="text-xs font-medium">{t.nom}</span>
                <button onClick={() => { setEditingId(t.id); setEditValue(t.nom); }} title="Renommer" data-testid={`tags-manager-edit-${t.id}`}><Pencil size={12} className="text-muted-foreground hover:text-primary" /></button>
                <button onClick={() => supprimer(t.id)} title="Supprimer" data-testid={`tags-manager-delete-${t.id}`}><Trash2 size={12} className="text-muted-foreground hover:text-destructive" /></button>
              </>
            )}
          </div>
        ))}
        {tags.length === 0 && <p className="text-xs text-muted-foreground">Aucun tag créé pour l'instant.</p>}
      </div>
    </div>
  );
}

// Config Stock (Administrateurs > Config Stock) : permet de simplifier le
// formulaire de création/modification d'article par boutique — masquer les
// types (article/formule/service) et champs optionnels non pertinents pour
// l'activité de la boutique, et activer/désactiver le "Stock infini". Lu
// par StockPage.jsx pour n'afficher que ce qui est effectivement pertinent.
function ConfigStockTab() {
  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState("");
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/shops");
    setShops(data);
    if (data.length > 0 && !selectedShopId) setSelectedShopId(data[0].id);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const shop = shops.find((s) => s.id === selectedShopId);
    if (shop) {
      setConfig(shop.stock_config || {
        types_actifs: { article: true, formule: true, service: true },
        champs_visibles: { perissable: true, vendu_au_poids: true, eco_participation: true, emplacement: true },
        stock_infini_active: false,
      });
    }
  }, [selectedShopId, shops]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/shops/${selectedShopId}`, { stock_config: config });
      toast.success("Configuration enregistrée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!config) return <p className="text-sm text-muted-foreground">Chargement...</p>;

  const TYPE_LABELS = { article: "Article normal", formule: "Formule / menu", service: "Service" };
  const CHAMP_LABELS = {
    perissable: "Article périssable",
    vendu_au_poids: "Vendu au poids",
    eco_participation: "Éco-participation",
    emplacement: "Emplacement du stock",
  };

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <label className="text-xs font-medium text-muted-foreground block mb-1">Boutique</label>
        <Select value={selectedShopId} onValueChange={setSelectedShopId}>
          <SelectTrigger data-testid="config-stock-shop-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <p className="text-sm font-semibold mb-1">Types d'article proposés</p>
        <p className="text-xs text-muted-foreground mb-3">
          Décochez un type pour qu'il n'apparaisse plus au choix lors de la création d'un article dans cette
          boutique. Les articles déjà créés avec ce type restent inchangés.
        </p>
        <div className="space-y-2">
          {Object.keys(TYPE_LABELS).map((key) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer" htmlFor={`config-stock-type-${key}`}>
              <Checkbox
                id={`config-stock-type-${key}`}
                data-testid={`config-stock-type-${key}`}
                checked={config.types_actifs[key]}
                onCheckedChange={(v) => setConfig({ ...config, types_actifs: { ...config.types_actifs, [key]: !!v } })}
              />
              <span className="text-sm">{TYPE_LABELS[key]}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold mb-1">Champs optionnels visibles</p>
        <p className="text-xs text-muted-foreground mb-3">
          Masque le champ correspondant sur le formulaire d'article — les valeurs déjà renseignées sur des
          articles existants sont conservées, seule la saisie est masquée.
        </p>
        <div className="space-y-2">
          {Object.keys(CHAMP_LABELS).map((key) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer" htmlFor={`config-stock-champ-${key}`}>
              <Checkbox
                id={`config-stock-champ-${key}`}
                data-testid={`config-stock-champ-${key}`}
                checked={config.champs_visibles[key]}
                onCheckedChange={(v) => setConfig({ ...config, champs_visibles: { ...config.champs_visibles, [key]: !!v } })}
              />
              <span className="text-sm">{CHAMP_LABELS[key]}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold mb-1">Stock infini</p>
        <label className="flex items-center gap-2 cursor-pointer" htmlFor="config-stock-infini-active">
          <Checkbox
            id="config-stock-infini-active"
            data-testid="config-stock-infini-active"
            checked={config.stock_infini_active}
            onCheckedChange={(v) => setConfig({ ...config, stock_infini_active: !!v })}
          />
          <span className="text-sm">Activer la case "Stock infini" sur le formulaire d'article</span>
        </label>
        <p className="text-xs text-muted-foreground mt-1">
          Un article marqué "Stock infini" n'est jamais considéré en rupture et sa quantité n'est jamais
          décrémentée à la vente.
        </p>
      </div>

      <Button onClick={save} disabled={saving} data-testid="config-stock-save">
        {saving ? "Enregistrement..." : "Enregistrer"}
      </Button>
    </div>
  );
}

function MargeTab() {
  const [percent, setPercent] = useState(5);
  const [draft, setDraft] = useState(5);
  const [saving, setSaving] = useState(false);

  const [autoPercent, setAutoPercent] = useState(50);
  const [autoDraft, setAutoDraft] = useState(50);
  const [autoSaving, setAutoSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/default-margin");
    setPercent(data.percent);
    setDraft(data.percent);
  };
  const loadAuto = async () => {
    const { data } = await api.get("/admin/auto-prix-vente-margin");
    setAutoPercent(data.percent);
    setAutoDraft(data.percent);
  };
  useEffect(() => { load(); loadAuto(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/default-margin", { percent: Number(draft) });
      toast.success("Taux de bénéfice par défaut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveAuto = async () => {
    setAutoSaving(true);
    try {
      await api.put("/admin/auto-prix-vente-margin", { percent: Number(autoDraft) });
      toast.success("Pourcentage de prix de vente automatique mis à jour");
      loadAuto();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAutoSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6">
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Dans les Statistiques, un article vendu sans prix d'achat renseigné n'est plus compté comme 100% de bénéfice.
          À la place, ce taux est appliqué au prix de vente TTC pour estimer le bénéfice (le reste étant considéré
          comme un coût implicite). Ce réglage ne concerne que les articles de stock — les prestations restent à 100%
          de bénéfice, n'ayant pas de prix d'achat par nature.
        </p>
        <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
          <Percent size={16} className="text-primary shrink-0" />
          <Input type="number" min={0} max={100} step={0.5} className="w-28" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="marge-defaut-input" />
          <span className="text-sm text-muted-foreground">% de bénéfice supposé (actuellement {percent}%)</span>
          <Button size="sm" onClick={save} disabled={saving || Number(draft) === percent} className="gap-1.5 ml-auto" data-testid="marge-defaut-save">
            <RotateCcw size={13} /> Enregistrer
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Quand un article arrive en stock sans prix de vente (0 € TTC) — typiquement à la réception d'une commande
          dépôt — un prix de vente est calculé automatiquement à partir du prix d'achat fournisseur H.T et de ce
          pourcentage : <span className="font-medium text-foreground">prix de vente H.T = prix d'achat H.T x (1 + pourcentage / 100)</span>.
          Un article dont le prix a été fixé de cette façon apparaît en rouge dans le Stock, avec un bandeau d'alerte,
          jusqu'à ce qu'il soit vérifié et validé manuellement.
        </p>
        <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
          <Percent size={16} className="text-primary shrink-0" />
          <Input type="number" min={0} max={1000} step={1} className="w-28" value={autoDraft} onChange={(e) => setAutoDraft(e.target.value)} data-testid="marge-auto-input" />
          <span className="text-sm text-muted-foreground">% ajouté au prix d'achat H.T (actuellement {autoPercent}%)</span>
          <Button size="sm" onClick={saveAuto} disabled={autoSaving || Number(autoDraft) === autoPercent} className="gap-1.5 ml-auto" data-testid="marge-auto-save">
            <RotateCcw size={13} /> Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}


function AuthEventsCard() {
  // Journal des connexions (audit conformité RGPD art. 5-2, accountability) :
  // complète les compteurs anti-bruteforce ci-dessus (qui sont ÉCRASÉS à
  // chaque tentative) par un historique consultable — qui s'est connecté,
  // quand, depuis quelle IP, avec quel résultat. Conservé 400 jours puis
  // purgé automatiquement (voir login_security.py) : ce n'est pas une
  // donnée fiscale, sa conservation indéfinie ne serait pas justifiée.
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/auth-events", { params: { limit: 200 } });
      setEvents(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec du chargement du journal");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const visible = showAll ? events : events.slice(0, 15);

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><History size={15} /> Journal des connexions</p>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="gap-1.5" data-testid="auth-events-refresh">
          <RotateCcw size={13} /> Actualiser
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Historique des tentatives de connexion (réussies et échouées), conservé 400 jours puis purgé
        automatiquement. Utile pour reconstituer qui s'est connecté quand en cas de contrôle ou d'incident.
      </p>
      <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="auth-events-list">
        {visible.map((e) => (
          <div key={e.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-1.5 text-xs">
            <div className="min-w-0">
              <span className="font-mono">{e.matricule}</span>{" "}
              {e.success
                ? <Badge variant="secondary">réussie</Badge>
                : <Badge variant="destructive">{e.reason?.replace(/_/g, " ")}</Badge>}
              {e.ip && <span className="text-muted-foreground ml-2">{e.ip}</span>}
            </div>
            <span className="text-muted-foreground shrink-0">{(e.created_at || "").replace("T", " ").slice(0, 19)}</span>
          </div>
        ))}
        {events.length === 0 && <p className="text-xs text-muted-foreground">Aucune tentative de connexion enregistrée.</p>}
      </div>
      {events.length > 15 && (
        <Button size="sm" variant="ghost" onClick={() => setShowAll((v) => !v)} data-testid="auth-events-toggle">
          {showAll ? "Réduire" : `Voir tout (${events.length})`}
        </Button>
      )}
    </div>
  );
}

function SecuriteTab() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/security-settings");
    setSettings(data);
    setDraft(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (Number(draft.lockout_minutes) > Number(draft.lockout_max_minutes)) {
      toast.error("La durée de blocage de base ne peut pas dépasser le plafond configuré");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/security-settings", {
        max_attempts: Number(draft.max_attempts),
        lockout_minutes: Number(draft.lockout_minutes),
        exponential: !!draft.exponential,
        lockout_max_minutes: Number(draft.lockout_max_minutes),
      });
      toast.success("Réglages de sécurité mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;
  const changed = JSON.stringify(draft) !== JSON.stringify(settings);

  // Aperçu des durées de blocage successives si le mode exponentiel est activé,
  // pour que l'admin visualise concrètement l'effet de ses réglages avant de les enregistrer.
  const previewCycles = draft.exponential
    ? Array.from({ length: 5 }).map((_, i) => Math.min(Number(draft.lockout_minutes) * 2 ** i, Number(draft.lockout_max_minutes)))
    : null;

  const fmtMinutes = (m) => {
    if (m < 60) return `${m} min`;
    if (m < 1440) return `${Math.round((m / 60) * 10) / 10} h`;
    return `${Math.round((m / 1440) * 10) / 10} j`;
  };

  return (
    <div className="max-w-xl space-y-6">
      <p className="text-sm text-muted-foreground">
        Depuis cette mise à jour, la connexion se fait par <strong>matricule</strong> (identifiant à 7 chiffres) puis
        <strong> code PIN</strong> (secret, 8 chiffres). Après un nombre configurable de tentatives échouées
        <em> consécutives</em> sur un même compte, celui-ci est bloqué temporairement — indépendamment des autres
        comptes. Un administrateur peut toujours débloquer un compte manuellement depuis l'onglet Utilisateurs
        (icône <LockKeyhole size={11} className="inline -mt-0.5" /> quand un compte est bloqué).
      </p>

      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div className="flex items-center gap-3">
          <Lock size={16} className="text-primary shrink-0" />
          <label className="text-sm flex-1">Tentatives échouées avant blocage</label>
          <Input type="number" min={1} max={20} className="w-24" value={draft.max_attempts}
            onChange={(e) => setDraft({ ...draft, max_attempts: e.target.value })} data-testid="security-max-attempts" />
        </div>
        <div className="flex items-center gap-3">
          <Lock size={16} className="text-primary shrink-0" />
          <label className="text-sm flex-1">Durée de blocage de base</label>
          <Input type="number" min={1} max={1440} className="w-24" value={draft.lockout_minutes}
            onChange={(e) => setDraft({ ...draft, lockout_minutes: e.target.value })} data-testid="security-lockout-minutes" />
          <span className="text-sm text-muted-foreground">min</span>
        </div>
        <div className="flex items-center gap-3">
          <Lock size={16} className="text-primary shrink-0" />
          <label className="text-sm flex-1">Plafond de blocage</label>
          <Input type="number" min={1} max={1440} className="w-24" value={draft.lockout_max_minutes}
            onChange={(e) => setDraft({ ...draft, lockout_max_minutes: e.target.value })} data-testid="security-lockout-max" />
          <span className="text-sm text-muted-foreground">min (1440 = 24h)</span>
        </div>
        <label className="flex items-start gap-2.5 text-sm pt-1">
          <Checkbox
            checked={!!draft.exponential}
            onCheckedChange={(v) => setDraft({ ...draft, exponential: !!v })}
            data-testid="security-exponential"
          />
          <span>
            Blocage exponentiel — chaque nouveau blocage double la durée du précédent (au lieu de rester fixe),
            jusqu'au plafond ci-dessus. Le niveau repart de zéro dès qu'une connexion réussit.
          </span>
        </label>

        {previewCycles && (
          <div className="text-xs text-muted-foreground bg-secondary rounded-lg px-3 py-2" data-testid="security-preview">
            Aperçu des blocages successifs : {previewCycles.map((m, i) => (
              <span key={i}>{i > 0 && " → "}<strong className="text-foreground">{fmtMinutes(m)}</strong></span>
            ))}
          </div>
        )}

        <Button size="sm" onClick={save} disabled={saving || !changed} className="gap-1.5" data-testid="security-save">
          <RotateCcw size={13} /> Enregistrer
        </Button>
      </div>

      {/* "E-mails internes (notifications)" (SiteSmtpCard) retiré côté site
          (demande explicite) : géré exclusivement depuis le panel
          d'hébergement désormais. */}
      <AuthEventsCard />
    </div>
  );
}

function FideliteTab() {
  // Réglages du programme de fidélité (voir fidelite_utils.py / routers/caisse_routes.py
  // GET-PATCH /caisse/fidelite-settings) — précédemment dans l'onglet Caisse >
  // Registre fiscal, déplacés ici pour regrouper tous les réglages
  // "boutique" côté Administration.
  const [fidelite, setFidelite] = useState(null); // { active, taux_points_par_euro, paliers_cartes_cadeaux }
  const [saving, setSaving] = useState(false);
  const [newPalierPoints, setNewPalierPoints] = useState("");
  const [newPalierMontant, setNewPalierMontant] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/caisse/fidelite-settings");
      setFidelite(data);
    } catch {
      // silencieux : boutique de travail pas encore sélectionnée
    }
  };
  useEffect(() => { load(); }, []);

  const persist = async (patch) => {
    const next = { ...fidelite, ...patch };
    setSaving(true);
    try {
      const { data } = await api.patch("/caisse/fidelite-settings", {
        fidelite_active: next.active,
        fidelite_taux_points_par_euro: Number(next.taux_points_par_euro),
        fidelite_paliers_cartes_cadeaux: next.paliers_cartes_cadeaux,
      });
      setFidelite({
        active: data.fidelite_active, taux_points_par_euro: data.fidelite_taux_points_par_euro,
        paliers_cartes_cadeaux: data.fidelite_paliers_cartes_cadeaux,
      });
      toast.success("Programme de fidélité mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addPalier = () => {
    const points = Number(newPalierPoints);
    const montant = Number(newPalierMontant);
    if (!points || points <= 0 || !montant || montant <= 0) {
      toast.error("Indiquez un nombre de points et un montant strictement positifs");
      return;
    }
    persist({ paliers_cartes_cadeaux: [...(fidelite.paliers_cartes_cadeaux || []), { points, montant }] });
    setNewPalierPoints("");
    setNewPalierMontant("");
  };

  const removePalier = (index) => {
    persist({ paliers_cartes_cadeaux: fidelite.paliers_cartes_cadeaux.filter((_, i) => i !== index) });
  };

  if (!fidelite) return null;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-xl border border-border bg-card p-4">
        <p className="font-heading font-semibold flex items-center gap-2 mb-1"><Star size={15} />Programme de fidélité</p>
        <p className="text-sm text-muted-foreground mb-3">
          Une fois activé, chaque vente rattachée à un client à la caisse lui fait gagner des points. Il peut ensuite
          les échanger, à la caisse, contre l'un des paliers configurés ci-dessous — soit en réduction immédiate sur
          la vente en cours, soit contre une carte cadeau (voir l'onglet Caisse &gt; Carte cadeau) : les deux usages
          partagent les mêmes paliers, réglés une seule fois ici.
        </p>
        <label className="flex items-center gap-2 text-sm mb-3" data-testid="admin-fidelite-active-label">
          <input
            type="checkbox"
            checked={fidelite.active}
            onChange={(e) => persist({ active: e.target.checked })}
            disabled={saving}
            data-testid="admin-fidelite-active-toggle"
          />
          Activer le programme de fidélité pour cette boutique
        </label>
        {fidelite.active && (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Points gagnés par euro TTC dépensé</label>
              <Input
                type="number" min="0" step="0.1" className="w-32"
                value={fidelite.taux_points_par_euro}
                onChange={(e) => setFidelite({ ...fidelite, taux_points_par_euro: e.target.value })}
                onBlur={() => persist({})}
                data-testid="admin-fidelite-taux-input"
              />
            </div>
            <p className="text-xs text-muted-foreground pb-2">
              Ex. : 100€ dépensés = {(100 * fidelite.taux_points_par_euro).toFixed(0)} points gagnés.
            </p>
          </div>
        )}
      </div>

      {fidelite.active && (
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="font-heading font-semibold mb-1">Paliers de points</p>
          <p className="text-sm text-muted-foreground mb-3">
            Chaque palier défini ici est proposé au client de deux façons : en réduction immédiate sur sa vente en
            cours à la caisse, ou en échange contre une carte cadeau (onglet Caisse &gt; Carte cadeau) — un seul
            réglage pour les deux usages (ex. 500 points = 30 €).
          </p>
          <div className="space-y-2 mb-3" data-testid="admin-fidelite-paliers-list">
            {(fidelite.paliers_cartes_cadeaux || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Aucun palier configuré pour l'instant.</p>
            )}
            {(fidelite.paliers_cartes_cadeaux || []).map((p, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`admin-fidelite-palier-${i}`}>
                <span className="flex-1"><strong>{p.points}</strong> points → <strong>{p.montant.toFixed(2)} €</strong></span>
                <button onClick={() => removePalier(i)} className="text-destructive" data-testid={`admin-fidelite-palier-delete-${i}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Points requis</label>
              <Input type="number" min="1" className="w-28" value={newPalierPoints} onChange={(e) => setNewPalierPoints(e.target.value)} data-testid="admin-fidelite-palier-points-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Montant (€)</label>
              <Input type="number" min="0.01" step="0.01" className="w-28" value={newPalierMontant} onChange={(e) => setNewPalierMontant(e.target.value)} data-testid="admin-fidelite-palier-montant-input" />
            </div>
            <Button size="sm" onClick={addPalier} disabled={saving} className="gap-1.5" data-testid="admin-fidelite-palier-add-button">
              <Plus size={14} />Ajouter
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function LogsTab() {
  // Journal des actions utilisateur (voir activity_log_utils.py) — toujours
  // sur la boutique de travail actuellement sélectionnée (ShopSwitcher en
  // haut de l'écran), jamais toutes boutiques confondues.
  const [modules, setModules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moduleFilter, setModuleFilter] = useState("__all__");
  const [matriculeFilter, setMatriculeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    api.get("/admin/logs/modules").then((r) => setModules(r.data)).catch(() => setModules([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (moduleFilter !== "__all__") params.module = moduleFilter;
      if (matriculeFilter.trim()) params.matricule = matriculeFilter.trim();
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      const { data } = await api.get("/admin/logs", { params });
      setLogs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [moduleFilter, matriculeFilter, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const MODULE_LABELS = {
    caisse: "Caisse", stock: "Stock", devis: "Devis", reprise: "Reprise", intervention: "Intervention",
    client: "Client", depot: "Dépôt", commande: "Commande", carte_cadeau: "Carte cadeau", fidelite: "Fidélité", utilisateur: "Utilisateur",
  };
  const ACTION_LABELS = {
    creation: "Création", modification: "Modification", suppression: "Suppression",
    cloture: "Clôture", annulation: "Annulation", avoir: "Avoir", envoi: "Envoi", validation: "Validation", decision: "Décision client",
  };

  const resetFilters = () => {
    setModuleFilter("__all__");
    setMatriculeFilter("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Module</label>
          <Select value={moduleFilter} onValueChange={setModuleFilter}>
            <SelectTrigger className="w-44" data-testid="logs-filter-module"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tous les modules</SelectItem>
              {modules.map((m) => (
                <SelectItem key={m} value={m}>{MODULE_LABELS[m] || m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Matricule</label>
          <Input
            placeholder="Ex : 1234567"
            value={matriculeFilter}
            onChange={(e) => setMatriculeFilter(e.target.value)}
            className="w-36"
            data-testid="logs-filter-matricule"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Du</label>
          <Input
            type="datetime-local"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-52"
            data-testid="logs-filter-date-from"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Au</label>
          <Input
            type="datetime-local"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-52"
            data-testid="logs-filter-date-to"
          />
        </div>
        <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="logs-filter-reset">Réinitialiser</Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="logs-table">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="p-3">Date / heure</th>
              <th className="p-3">Matricule</th>
              <th className="p-3">Utilisateur</th>
              <th className="p-3">Module</th>
              <th className="p-3">Action</th>
              <th className="p-3">Document</th>
              <th className="p-3">Détail</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">Chargement...</td></tr>
            )}
            {!loading && logs.length === 0 && (
              <tr><td colSpan={7} className="p-4 text-center text-muted-foreground" data-testid="logs-empty">Aucune action journalisée pour ces filtres.</td></tr>
            )}
            {!loading && logs.map((l) => (
              <tr key={l.id} className="border-t border-border" data-testid={`logs-row-${l.id}`}>
                <td className="p-3 text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString("fr-FR")}</td>
                <td className="p-3 font-mono">{l.matricule}</td>
                <td className="p-3">{l.user_nom}</td>
                <td className="p-3">{MODULE_LABELS[l.module] || l.module}</td>
                <td className="p-3">{ACTION_LABELS[l.action] || l.action}</td>
                <td className="p-3 text-muted-foreground">{l.target_numero || "—"}</td>
                <td className="p-3 text-muted-foreground">{l.detail || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaymentMethodsTab() {
  const [methods, setMethods] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/payment-methods");
    setMethods(data);
    const d = {};
    data.forEach((m) => { d[m.key] = { label: m.label, enabled: m.enabled }; });
    setDrafts(d);
  };
  useEffect(() => { load(); }, []);

  const setField = (key, field, value) => {
    setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  };

  const dirty = methods.some((m) => {
    const d = drafts[m.key];
    return !d || d.label !== m.label || d.enabled !== m.enabled;
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { methods: methods.map((m) => ({ key: m.key, label: drafts[m.key]?.label ?? m.label, enabled: drafts[m.key]?.enabled ?? m.enabled })) };
      await api.put("/admin/payment-methods", payload);
      toast.success("Moyens de paiement mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-4">
      <p className="text-sm text-muted-foreground">
        Choisissez les moyens de paiement proposés à la Caisse lors de la validation d'un panier. Un moyen désactivé
        n'apparaît plus à l'encaissement, mais reste affiché tel quel sur les documents déjà émis. Le libellé peut être
        personnalisé (par ex. renommer « Autre » en « Virement » ou « Ticket restaurant »).
      </p>
      <div className="space-y-2" data-testid="payment-methods-list">
        {methods.map((m) => {
          const draft = drafts[m.key] || { label: m.label, enabled: m.enabled };
          return (
            <div key={m.key} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`payment-method-row-${m.key}`}>
              <button
                type="button"
                onClick={() => setField(m.key, "enabled", !draft.enabled)}
                data-testid={`payment-method-toggle-${m.key}`}
                className={`shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center transition-colors ${draft.enabled ? "bg-primary/10 border-primary text-primary" : "border-border text-muted-foreground"}`}
                title={draft.enabled ? "Activé — cliquer pour désactiver" : "Désactivé — cliquer pour activer"}
              >
                {draft.enabled ? <Check size={16} /> : <XIcon size={16} />}
              </button>
              <Input
                data-testid={`payment-method-label-${m.key}`}
                value={draft.label}
                onChange={(e) => setField(m.key, "label", e.target.value)}
                className="flex-1"
              />
            </div>
          );
        })}
      </div>
      <Button size="sm" onClick={save} disabled={saving || !dirty} className="gap-1.5" data-testid="payment-methods-save">
        <RotateCcw size={13} /> Enregistrer
      </Button>
    </div>
  );
}

const ANOMALY_LABELS = {
  duplicate_seq: "Numéro de séquence en double",
  sequence_gap: "Trou dans la séquence",
  broken_chain: "Chaînage rompu",
  hash_mismatch: "Empreinte invalide (contenu modifié)",
  orphan_event: "Référence introuvable",
  ticket_without_hash: "Vente sans empreinte fiscale",
};


function AuditTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const runAudit = async () => {
    setLoading(true);
    setError("");
    try {
      // Comme /caisse/verify-chain et /caisse/cumuls : porte sur la boutique de
      // travail actuellement sélectionnée par l'utilisateur (voir sélecteur de
      // boutique dans l'en-tête), pas sur un paramètre choisi ici.
      const { data } = await api.get("/caisse/audit");
      setReport(data);
    } catch (e) {
      setReport(null);
      setError(formatApiError(e.response?.data?.detail) || "Échec de l'audit");
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-conformite-${report.shop_id}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-muted-foreground">
        Lance une vérification complète du registre fiscal (chaînage cryptographique, continuité de la séquence,
        cohérence des références, empreintes des ventes) de la <strong>boutique de travail actuellement
        sélectionnée</strong> (voir le sélecteur de boutique en haut de l'application). Cet audit technique
        n'est ni une certification par un organisme accrédité, ni un avis juridique — voir l'onglet Attestation
        de la Caisse pour la portée exacte de la conformité.
      </p>

      <div className="flex items-end gap-3 flex-wrap">
        <Button onClick={runAudit} disabled={loading} data-testid="audit-run-btn" className="gap-1.5">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          Lancer l'audit
        </Button>
        {report && (
          <Button variant="outline" onClick={exportReport} data-testid="audit-export-btn" className="gap-1.5">
            <Download size={14} /> Exporter (JSON)
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-destructive" data-testid="audit-error">{error}</p>}

      {/* "Configuration de l'attestation individuelle (éditeur)"
          (AttestationConfigCard) retirée côté site (demande explicite) :
          gérée exclusivement depuis le panel d'hébergement désormais. */}

      {report && (
        <div className="space-y-4" data-testid="audit-report">
          <div className={`rounded-xl border p-4 flex items-center gap-3 ${report.valid ? "border-emerald-600/30 bg-emerald-600/5" : "border-destructive/40 bg-destructive/5"}`}>
            {report.valid ? <ShieldCheck size={22} className="text-emerald-600 shrink-0" /> : <ShieldAlert size={22} className="text-destructive shrink-0" />}
            <div>
              <p className="font-heading font-semibold" data-testid="audit-status">
                {report.valid ? "Registre fiscal intègre" : `${report.anomalies?.length ?? 0} anomalie(s) détectée(s)`}
              </p>
              <p className="text-xs text-muted-foreground">
                {report.nb_events} événement(s) — séquence {report.first_seq ?? "—"} à {report.last_seq ?? "—"}
              </p>
            </div>
          </div>

          {report.tickets_missing_hash > 0 && (
            <p className="text-sm text-destructive">
              {report.tickets_missing_hash} vente(s) sans empreinte fiscale enregistrée.
            </p>
          )}

          {report.anomalies?.length > 0 && (
            <div className="space-y-2">
              <p className="font-heading font-semibold text-sm">Anomalies</p>
              <div className="space-y-1.5" data-testid="audit-anomalies-list">
                {report.anomalies.map((a, i) => (
                  <div key={i} className="rounded-lg border border-destructive/30 bg-destructive/5 p-2.5 text-sm">
                    <span className="font-medium">{ANOMALY_LABELS[a.type] || a.type}</span>
                    {a.seq != null && <span className="text-muted-foreground"> — séquence {a.seq}</span>}
                    <p className="text-xs text-muted-foreground mt-0.5">{a.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.numbering_gaps?.length > 0 && (
            <div className="space-y-2">
              <p className="font-heading font-semibold text-sm">Numéros réservés puis abandonnés</p>
              <p className="text-xs text-muted-foreground">
                Un numéro n'est jamais réutilisé : ces trous, dus à un échec technique après réservation, sont
                normaux dès lors qu'ils sont expliqués ici.
              </p>
              <div className="space-y-1.5" data-testid="audit-gaps-list">
                {report.numbering_gaps.map((g) => (
                  <div key={g.id} className="rounded-lg border border-border bg-card p-2.5 text-sm">
                    <span className="font-mono font-medium">{g.numero}</span>
                    <span className="text-muted-foreground"> — {g.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-4">
            <p className="font-heading font-semibold text-sm mb-2">Cumuls perpétuels</p>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><p className="text-muted-foreground text-xs">Total H.T</p><p className="font-medium">{(report.cumuls?.total_ht ?? 0).toFixed(2)} €</p></div>
              <div><p className="text-muted-foreground text-xs">Total TVA</p><p className="font-medium">{(report.cumuls?.total_tva ?? 0).toFixed(2)} €</p></div>
              <div><p className="text-muted-foreground text-xs">Total TTC</p><p className="font-medium">{(report.cumuls?.total_ttc ?? 0).toFixed(2)} €</p></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CONFORMITÉ FISCALE — mode Test/École, version, attestation, rétention, audit
// global (voir backend/compliance_utils.py). Porte, comme l'onglet "Audit de
// conformité" existant, sur la boutique de travail actuellement sélectionnée.
// ---------------------------------------------------------------------------

function ModeSwitchDialog({ open, onOpenChange, targetMode, onConfirm, loading }) {
  const [confirmation, setConfirmation] = useState("");
  const expected = targetMode === "test" ? "ACTIVER LE MODE TEST" : "REVENIR EN PRODUCTION";
  useEffect(() => { if (open) setConfirmation(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{targetMode === "test" ? "Activer le mode Test / École" : "Revenir en mode Production"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            {targetMode === "test"
              ? "Toutes les prochaines ventes, avoirs, sessions de caisse et clôtures de cette boutique seront enregistrés dans un environnement de TEST totalement isolé de la production (séquence, chaînage, cumuls, stock, clôtures et exports séparés). Le stock réel ne sera jamais impacté."
              : "La boutique reprendra ses ventes en environnement de PRODUCTION. Les données de test resteront consultables séparément (Administrateurs > Conformité fiscale) mais ne seront plus jamais mélangées aux données réelles."}
          </p>
          <p className="font-medium">Pour confirmer, saisissez exactement : <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{expected}</span></p>
          <Input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} data-testid="mode-confirmation-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            variant={targetMode === "test" ? "default" : "default"}
            disabled={confirmation.trim() !== expected || loading}
            onClick={() => onConfirm(confirmation)}
            data-testid="mode-confirm-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : "Confirmer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModeCard() {
  const [mode, setMode] = useState(null);
  const [dialogTarget, setDialogTarget] = useState(null);
  const [switching, setSwitching] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/caisse/mode");
      setMode(data);
    } catch { /* boutique non sélectionnée */ }
  };
  useEffect(() => { load(); }, []);

  const confirmSwitch = async (confirmation) => {
    setSwitching(true);
    try {
      await api.post("/caisse/mode", { mode: dialogTarget, confirmation });
      toast.success(dialogTarget === "test" ? "Mode Test / École activé" : "Retour en mode Production effectué");
      setDialogTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec du changement de mode");
    } finally {
      setSwitching(false);
    }
  };

  if (!mode) return null;
  const isTest = mode.mode === "test";
  return (
    <div className={`rounded-xl border p-4 ${isTest ? "border-amber-500/50 bg-amber-500/10" : "border-border bg-card"}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FlaskConical size={18} className={isTest ? "text-amber-600" : "text-muted-foreground"} />
          <div>
            <p className="font-heading font-semibold text-sm">
              Mode actuel : {isTest ? <Badge className="bg-amber-500 text-white ml-1">TEST / ÉCOLE</Badge> : <Badge variant="secondary" className="ml-1">PRODUCTION</Badge>}
            </p>
            {mode.changed_at && (
              <p className="text-xs text-muted-foreground">Depuis le {new Date(mode.changed_at).toLocaleString("fr-FR")} par {mode.changed_by}</p>
            )}
          </div>
        </div>
        <Button size="sm" variant={isTest ? "default" : "outline"} onClick={() => setDialogTarget(isTest ? "production" : "test")} data-testid="mode-switch-btn">
          {isTest ? "Revenir en Production" : "Activer le mode Test / École"}
        </Button>
      </div>
      <ModeSwitchDialog open={!!dialogTarget} onOpenChange={(v) => !v && setDialogTarget(null)} targetMode={dialogTarget} onConfirm={confirmSwitch} loading={switching} />
    </div>
  );
}

function VersionRegistryCard() {
  const [versions, setVersions] = useState([]);
  const [form, setForm] = useState({ version: "", change_type: "patch", description: "", fiscal_impact: false, breaking: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/compliance/versions");
      setVersions(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.version.trim()) { toast.error("Le numéro de version est obligatoire"); return; }
    setSaving(true);
    try {
      await api.post("/admin/compliance/versions", form);
      toast.success("Version enregistrée");
      setForm({ version: "", change_type: "patch", description: "", fiscal_impact: false, breaking: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const activate = async (id) => {
    try {
      await api.post(`/admin/compliance/versions/${id}/activate`);
      toast.success("Version activée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'activation");
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><GitBranch size={15} /> Registre de version (MAJEURE.MINEURE.CORRECTIF)</p>
      <p className="text-xs text-muted-foreground">
        Un changement à impact fiscal ne peut jamais être déclaré comme correctif (patch) ; un changement qui
        rompt la compatibilité d'une attestation déjà émise doit être déclaré majeur. Une version à impact
        fiscal ne peut être activée qu'une fois couverte par une attestation valide.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
        <div className="space-y-1"><label className="text-xs text-muted-foreground">Version</label><Input placeholder="1.2.0" value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} data-testid="version-input" /></div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Type</label>
          <Select value={form.change_type} onValueChange={(v) => setForm((f) => ({ ...f, change_type: v }))}>
            <SelectTrigger data-testid="version-change-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="patch">Correctif (patch)</SelectItem>
              <SelectItem value="minor">Mineure (minor)</SelectItem>
              <SelectItem value="major">Majeure (major)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 md:col-span-2"><label className="text-xs text-muted-foreground">Description</label><Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
        <Button onClick={create} disabled={saving} data-testid="version-create-btn" className="gap-1.5"><Plus size={14} /> Enregistrer</Button>
      </div>
      <div className="flex gap-4 text-sm">
        <label className="flex items-center gap-1.5"><Checkbox checked={form.fiscal_impact} onCheckedChange={(v) => setForm((f) => ({ ...f, fiscal_impact: !!v }))} /> Impact fiscal</label>
        <label className="flex items-center gap-1.5"><Checkbox checked={form.breaking} onCheckedChange={(v) => setForm((f) => ({ ...f, breaking: !!v }))} /> Rupture de compatibilité</label>
      </div>
      <div className="space-y-1.5" data-testid="version-list">
        {versions.map((v) => (
          <div key={v.id} className="flex items-center justify-between rounded-lg border border-border p-2.5 text-sm">
            <div>
              <span className="font-mono font-medium">{v.version}</span>{" "}
              {v.activated && <Badge variant="secondary">activée</Badge>}{" "}
              {v.fiscal_impact && <Badge className="bg-amber-500/80 text-white">impact fiscal</Badge>}
              <p className="text-xs text-muted-foreground">{v.description}</p>
            </div>
            {!v.activated && <Button size="sm" variant="outline" onClick={() => activate(v.id)}>Activer</Button>}
          </div>
        ))}
        {versions.length === 0 && <p className="text-xs text-muted-foreground">Aucune version enregistrée.</p>}
      </div>
    </div>
  );
}

const EMPTY_ATTESTATION_SHOP_FORM = {
  nom: "", adresse: "", code_postal: "", ville: "", siret: "",
  forme_juridique: "", capital_social: "", rcs_ville: "", tva_intracom: "",
};

function AttestationsCard() {
  const { user } = useAuth();
  const [attestations, setAttestations] = useState([]);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [shopFormOpen, setShopFormOpen] = useState(false);
  const [shopForm, setShopForm] = useState(EMPTY_ATTESTATION_SHOP_FORM);
  const [shopFormBusy, setShopFormBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/caisse/compliance/attestations");
      setAttestations(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  // Correctif demandé : quand la création échoue parce que l'identité de la
  // boutique utilisatrice (Volet 2) est incomplète, on ouvre directement un
  // menu avec les champs à remplir plutôt que de simplement afficher un
  // message d'erreur renvoyant l'admin naviguer vers un autre onglet.
  const openShopForm = async () => {
    if (!user?.effective_shop_id) return;
    try {
      const { data } = await api.get("/shops");
      const shop = (data || []).find((s) => s.id === user.effective_shop_id);
      setShopForm({
        nom: shop?.nom || "", adresse: shop?.adresse || "", code_postal: shop?.code_postal || "",
        ville: shop?.ville || "", siret: shop?.siret || "", forme_juridique: shop?.forme_juridique || "",
        capital_social: shop?.capital_social || "", rcs_ville: shop?.rcs_ville || "", tva_intracom: shop?.tva_intracom || "",
      });
    } catch {
      setShopForm(EMPTY_ATTESTATION_SHOP_FORM);
    }
    setShopFormOpen(true);
  };

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/caisse/compliance/attestations");
      toast.success("Attestation générée");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = formatApiError(detail);
      if (typeof detail === "string" && (detail.includes("Volet 2") || detail.includes("entreprise utilisatrice"))) {
        toast.error("Complétez les informations de la boutique pour générer l'attestation");
        openShopForm();
      } else {
        toast.error(message || "Échec de la génération");
      }
    } finally {
      setBusy(false);
    }
  };

  const saveShopAndGenerate = async () => {
    if (!user?.effective_shop_id) return;
    setShopFormBusy(true);
    try {
      await api.patch(`/shops/${user.effective_shop_id}`, shopForm);
      setShopFormOpen(false);
      toast.success("Boutique mise à jour");
      await generate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de la mise à jour de la boutique");
    } finally {
      setShopFormBusy(false);
    }
  };

  const revoke = async () => {
    if (!motif.trim()) { toast.error("Le motif de révocation est obligatoire"); return; }
    setBusy(true);
    try {
      await api.post(`/caisse/compliance/attestations/${revokeTarget}/revoke`, { motif });
      toast.success("Attestation révoquée");
      setRevokeTarget(null);
      setMotif("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de la révocation");
    } finally {
      setBusy(false);
    }
  };

  // Deux boutons distincts, comme demandé : le premier crée une nouvelle
  // attestation, le second ouvre un menu listant TOUTES les attestations de
  // la boutique (valides ET révoquées), chacune téléchargeable en PDF via
  // GET /caisse/attestation/{attestation_id}/pdf.
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><FileBadge size={15} /> Attestations de conformité</p>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={generate} disabled={busy} data-testid="attestation-generate-btn" className="gap-1.5">
            <Plus size={14} /> Créer une attestation de conformité
          </Button>
          <Button size="sm" variant="outline" onClick={() => { load(); setHistoryOpen(true); }} data-testid="attestation-history-btn" className="gap-1.5">
            <History size={14} /> Attestations de la boutique ({attestations.length})
          </Button>
        </div>
      </div>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Attestations de conformité de la boutique</DialogTitle></DialogHeader>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto" data-testid="attestation-history-list">
            {attestations.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-lg border border-border p-2.5 text-sm gap-2">
                <div className="min-w-0">
                  <span className="font-mono font-medium">{a.attestation_id}</span>{" "}
                  {a.revoked ? <Badge variant="destructive">révoquée</Badge> : <Badge variant="secondary">valide</Badge>}
                  <p className="text-xs text-muted-foreground">Émise le {(a.created_at || "").slice(0, 10)} — empreinte {a.attestation_hash?.slice(0, 12)}…</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <a href={`${api.defaults.baseURL}/caisse/attestation/${a.attestation_id}/pdf`} target="_blank" rel="noreferrer" title="Télécharger le PDF">
                    <Button size="sm" variant="outline" data-testid={`attestation-download-${a.attestation_id}`} className="gap-1.5">
                      <Download size={14} /> PDF
                    </Button>
                  </a>
                  {!a.revoked && (
                    <Button size="sm" variant="destructive" onClick={() => setRevokeTarget(a.attestation_id)}>Révoquer</Button>
                  )}
                </div>
              </div>
            ))}
            {attestations.length === 0 && <p className="text-xs text-muted-foreground">Aucune attestation émise pour cette boutique.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={shopFormOpen} onOpenChange={setShopFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Informations de la boutique utilisatrice (Volet 2)</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Le nom, l'adresse, la ville et le SIRET sont obligatoires pour générer une attestation : ils
            identifient l'entreprise qui utilise le logiciel dans le document officiel.
          </p>
          <div className="grid grid-cols-1 gap-2">
            <Input data-testid="attestation-shop-form-nom" placeholder="Nom de la boutique *" value={shopForm.nom} onChange={(e) => setShopForm((f) => ({ ...f, nom: e.target.value }))} />
            <Input data-testid="attestation-shop-form-adresse" placeholder="Adresse (rue, n°) *" value={shopForm.adresse} onChange={(e) => setShopForm((f) => ({ ...f, adresse: e.target.value }))} />
            <div className="grid grid-cols-3 gap-2">
              <Input data-testid="attestation-shop-form-code-postal" placeholder="Code postal" className="col-span-1" value={shopForm.code_postal} onChange={(e) => setShopForm((f) => ({ ...f, code_postal: e.target.value }))} />
              <Input data-testid="attestation-shop-form-ville" placeholder="Ville *" className="col-span-2" value={shopForm.ville} onChange={(e) => setShopForm((f) => ({ ...f, ville: e.target.value }))} />
            </div>
            <Input data-testid="attestation-shop-form-siret" placeholder="N° SIRET *" value={shopForm.siret} onChange={(e) => setShopForm((f) => ({ ...f, siret: e.target.value }))} />
            <Input data-testid="attestation-shop-form-forme-juridique" placeholder="Forme juridique (ex: SAS) — facultatif" value={shopForm.forme_juridique} onChange={(e) => setShopForm((f) => ({ ...f, forme_juridique: e.target.value }))} />
            <Input data-testid="attestation-shop-form-capital-social" placeholder="Capital social (ex: 15 000 €) — facultatif" value={shopForm.capital_social} onChange={(e) => setShopForm((f) => ({ ...f, capital_social: e.target.value }))} />
            <Input data-testid="attestation-shop-form-rcs-ville" placeholder="Ville du RCS — facultatif" value={shopForm.rcs_ville} onChange={(e) => setShopForm((f) => ({ ...f, rcs_ville: e.target.value }))} />
            <Input data-testid="attestation-shop-form-tva-intracom" placeholder="N° TVA intracommunautaire — facultatif" value={shopForm.tva_intracom} onChange={(e) => setShopForm((f) => ({ ...f, tva_intracom: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShopFormOpen(false)}>Annuler</Button>
            <Button disabled={shopFormBusy} onClick={saveShopAndGenerate} data-testid="attestation-shop-form-save-btn">Enregistrer et créer l'attestation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revokeTarget} onOpenChange={(v) => !v && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Révoquer l'attestation {revokeTarget}</DialogTitle></DialogHeader>
          <Textarea placeholder="Motif de révocation (obligatoire)" value={motif} onChange={(e) => setMotif(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>Annuler</Button>
            <Button variant="destructive" disabled={busy} onClick={revoke}>Révoquer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RetentionCard() {
  const [policy, setPolicy] = useState(null);
  useEffect(() => {
    api.get("/caisse/compliance/retention").then(({ data }) => setPolicy(data)).catch(() => {});
  }, []);
  if (!policy) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-2">
      <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><Archive size={15} /> Politique de conservation légale</p>
      <p className="text-sm">Durée retenue : <strong>{policy.retention_years} ans</strong> à compter de chaque document.</p>
      <p className="text-xs text-muted-foreground">
        C'est la plus longue des deux durées légales applicables à un ticket/une facture de caisse :{" "}
        {policy.retention_years_fiscal ?? 6} ans (délai de reprise fiscal, LPF art. L102 B) et{" "}
        {policy.retention_years_comptable ?? 10} ans (pièces justificatives comptables, Code de commerce art. L123-22).
      </p>
      <p className="text-xs text-muted-foreground">
        Tickets conservés : {policy.counts?.tickets ?? 0} — Clôtures conservées : {policy.counts?.clotures ?? 0}
        {policy.oldest_retention_until && ` — conservation la plus ancienne jusqu'au ${policy.oldest_retention_until.slice(0, 10)}`}
      </p>
      <p className="text-xs text-muted-foreground">{policy.note}</p>
    </div>
  );
}

function ConsolidatedComplianceCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/compliance/consolidated");
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'audit consolidé");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><Building2 size={15} /> Vue consolidée — toutes les boutiques</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Une seule entreprise (un seul SIREN) peut exploiter plusieurs boutiques : cette vue agrège l'audit de
            conformité de toutes les boutiques actives, en plus (pas à la place) de l'audit par boutique ci-dessus.
          </p>
        </div>
        <Button size="sm" onClick={run} disabled={loading} data-testid="consolidated-audit-run-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Lancer"}
        </Button>
      </div>
      {report && (
        <div data-testid="consolidated-audit-report" className="space-y-2">
          <div className={`rounded-lg border p-3 text-sm ${report.conforme ? "border-emerald-600/30 bg-emerald-600/5" : "border-destructive/40 bg-destructive/5"}`}>
            {report.nb_boutiques_conformes}/{report.nb_boutiques} boutique(s) conforme(s)
            {report.conforme ? " — ensemble de l'entreprise conforme." : "."}
          </div>
          {report.boutiques?.filter((b) => !b.conforme).length > 0 && (
            <div className="space-y-1.5">
              {report.boutiques.filter((b) => !b.conforme).map((b) => (
                <div key={b.shop_id} className="text-sm border border-destructive/30 rounded-lg p-2">
                  <p className="font-medium">{b.shop_nom || b.shop_id}</p>
                  <ul className="list-disc pl-5 text-xs text-muted-foreground">
                    {b.issues.map((i, idx) => <li key={idx}>{i}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EreportingExportCard() {
  const today = new Date().toISOString().slice(0, 10);
  const [debut, setDebut] = useState(`${today.slice(0, 4)}-01-01`);
  const [fin, setFin] = useState(today);
  const [dataset, setDataset] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/compliance/ereporting-export", {
        params: { debut: `${debut}T00:00:00+00:00`, fin: `${fin}T23:59:59+00:00` },
      });
      setDataset(data);
      toast.success("Jeu de données e-reporting généré");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de la génération");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!dataset) return;
    const rows = [["boutique", "document_numero", "document_type", "date_operation", "nature", "taux_tva", "montant_ht", "montant_ttc"]];
    dataset.boutiques.forEach((b) => {
      b.lignes.forEach((l) => {
        rows.push([b.shop_id, l.document_numero, l.document_type, l.date_operation, l.nature, l.taux_tva, l.montant_ht, l.montant_ttc]);
      });
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `e-reporting-${debut}-au-${fin}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div>
        <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><FileSpreadsheet size={15} /> Préparation e-reporting (ventes B2C)</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Prépare le jeu de données consolidé (montant, nature, taux de TVA, dates) attendu par l'administration au
          titre de l'e-reporting — obligation PME au 1ᵉʳ septembre 2027. Rien n'est transmis automatiquement : aucune
          plateforme agréée (PDP) n'est raccordée ici, ce n'est qu'un export préparatoire.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Du</label>
          <Input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Au</label>
          <Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
        </div>
        <Button size="sm" onClick={generate} disabled={loading} data-testid="ereporting-generate-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Générer"}
        </Button>
        {dataset && (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={downloadCsv}>
            <Download size={14} /> Télécharger le CSV
          </Button>
        )}
      </div>
      {dataset && (
        <p className="text-xs text-muted-foreground" data-testid="ereporting-summary">
          {dataset.nb_boutiques} boutique(s) — {dataset.nb_documents_total} document(s) — {dataset.nb_lignes_total} ligne(s) de vente.
        </p>
      )}
    </div>
  );
}

function GlobalComplianceAuditCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/caisse/compliance/audit");
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'audit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><History size={15} /> Audit global de conformité</p>
        <Button size="sm" onClick={run} disabled={loading} data-testid="global-audit-run-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Lancer l'audit"}
        </Button>
      </div>
      {report && (
        <div data-testid="global-audit-report" className="space-y-2">
          <div className={`rounded-lg border p-3 text-sm ${report.conforme ? "border-emerald-600/30 bg-emerald-600/5" : "border-destructive/40 bg-destructive/5"}`}>
            {report.conforme ? "Conforme : aucune anomalie détectée." : `${report.issues.length} point(s) à corriger.`}
          </div>
          {report.issues?.length > 0 && (
            <ul className="text-sm list-disc pl-5 space-y-1">
              {report.issues.map((i, idx) => <li key={idx}>{i}</li>)}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">
            {report.acquisition_date_missing
              ? "Date d'acquisition du logiciel non renseignée sur cette boutique (Administrateurs > Boutiques & Dépôts)."
              : "Date d'acquisition du logiciel renseignée sur cette boutique."}
          </p>
        </div>
      )}
    </div>
  );
}

function CompanyLegalConfigCard() {
  // Coordonnées propres à l'ENTREPRISE (pas à une boutique en particulier,
  // voir admin_routes.py COMPANY_LEGAL_CONFIG_SETTINGS_ID) : contact RGPD et
  // hébergeur des données. Jamais pré-remplies : tant qu'elles ne sont pas
  // renseignées ici, la page ne peut afficher qu'une mention "à compléter"
  // plutôt qu'une identité inventée.
  const EMPTY = { dpo_email: "", hebergeur_nom: "", hebergeur_adresse: "" };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/company-legal-config");
    setForm({ dpo_email: data.dpo_email || "", hebergeur_nom: data.hebergeur_nom || "", hebergeur_adresse: data.hebergeur_adresse || "" });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/company-legal-config", form);
      toast.success("Coordonnées RGPD / mentions légales enregistrées");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><Building2 size={15} /> RGPD & mentions légales de l'entreprise</p>
      <p className="text-xs text-muted-foreground">
        Contact pour l'exercice des droits RGPD (accès, rectification, effacement, portabilité, opposition) et
        identité de l'hébergeur des données (obligation LCEN art. 6-III). Propre à l'entreprise dans son ensemble,
        pas à une boutique en particulier.
      </p>
      <Input data-testid="rgpd-dpo-email" placeholder="Contact RGPD (e-mail)" value={form.dpo_email} onChange={(e) => setForm({ ...form, dpo_email: e.target.value })} />
      <Input data-testid="rgpd-hebergeur-nom" placeholder="Raison sociale de l'hébergeur des données" value={form.hebergeur_nom} onChange={(e) => setForm({ ...form, hebergeur_nom: e.target.value })} />
      <Input data-testid="rgpd-hebergeur-adresse" placeholder="Adresse de l'hébergeur" value={form.hebergeur_adresse} onChange={(e) => setForm({ ...form, hebergeur_adresse: e.target.value })} />
      <Button size="sm" onClick={save} disabled={saving} className="gap-1.5" data-testid="rgpd-config-save">
        <RotateCcw size={13} /> Enregistrer
      </Button>
    </div>
  );
}

function RgpdIncidentsCard() {
  // Registre des violations de données (art. 33§5 du RGPD) : documente TOUTE
  // violation, même celles non notifiées à la CNIL faute de risque pour les
  // personnes — obligation distincte de la notification elle-même.
  const EMPTY = {
    date_survenue: new Date().toISOString().slice(0, 10), nature: "", donnees_concernees: "",
    personnes_concernees: "", mesures_prises: "", notifie_cnil: false, notifie_personnes: false,
  };
  const [incidents, setIncidents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/rgpd/incidents");
      setIncidents(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.nature.trim()) { toast.error("La nature de l'incident est obligatoire"); return; }
    setSaving(true);
    try {
      await api.post("/admin/rgpd/incidents", form);
      toast.success("Incident consigné dans le registre");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><ShieldAlert size={15} /> Registre des incidents RGPD</p>
        <Button size="sm" onClick={() => setOpen(true)} className="gap-1.5" data-testid="rgpd-incident-create-btn"><Plus size={14} /> Consigner un incident</Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Toute violation de données (perte, accès non autorisé, divulgation...) doit être consignée ici, y compris
        celle que vous choisissez de ne pas notifier à la CNIL faute de risque pour les personnes concernées.
      </p>
      <div className="space-y-1.5" data-testid="rgpd-incidents-list">
        {incidents.map((i) => (
          <div key={i.id} className="rounded-lg border border-border p-2.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium">{i.nature}</span>
              <span className="text-xs text-muted-foreground">{i.date_survenue}</span>
            </div>
            <div className="flex gap-1.5 mt-1">
              <Badge variant={i.notifie_cnil ? "secondary" : "outline"}>{i.notifie_cnil ? "CNIL notifiée" : "CNIL non notifiée"}</Badge>
              <Badge variant={i.notifie_personnes ? "secondary" : "outline"}>{i.notifie_personnes ? "Personnes informées" : "Personnes non informées"}</Badge>
            </div>
          </div>
        ))}
        {incidents.length === 0 && <p className="text-xs text-muted-foreground">Aucun incident consigné.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Consigner un incident RGPD</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input type="date" value={form.date_survenue} onChange={(e) => setForm({ ...form, date_survenue: e.target.value })} data-testid="rgpd-incident-date" />
            <Input placeholder="Nature de l'incident (obligatoire)" value={form.nature} onChange={(e) => setForm({ ...form, nature: e.target.value })} data-testid="rgpd-incident-nature" />
            <Input placeholder="Catégories de données concernées" value={form.donnees_concernees} onChange={(e) => setForm({ ...form, donnees_concernees: e.target.value })} data-testid="rgpd-incident-donnees" />
            <Input placeholder="Personnes concernées (nombre/catégorie estimés)" value={form.personnes_concernees} onChange={(e) => setForm({ ...form, personnes_concernees: e.target.value })} data-testid="rgpd-incident-personnes" />
            <Textarea placeholder="Mesures correctives prises ou prévues" value={form.mesures_prises} onChange={(e) => setForm({ ...form, mesures_prises: e.target.value })} data-testid="rgpd-incident-mesures" />
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.notifie_cnil} onCheckedChange={(v) => setForm({ ...form, notifie_cnil: !!v })} /> CNIL notifiée</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.notifie_personnes} onCheckedChange={(v) => setForm({ ...form, notifie_personnes: !!v })} /> Personnes concernées informées</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button disabled={saving} onClick={create} data-testid="rgpd-incident-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ComplianceFiscaleTab() {
  // Restreint (demande explicite) aux deux seules cartes encore pilotées
  // depuis le site : Politique de conservation légale et Préparation
  // e-reporting (ventes B2C). Mode Test/École, Audit global de conformité,
  // Vue consolidée, Attestations de conformité, Registre de version et
  // RGPD & mentions légales de l'entreprise sont retirés d'ici — gérés
  // exclusivement depuis le panel d'hébergement désormais. Les composants
  // eux-mêmes (ModeCard, GlobalComplianceAuditCard, etc.) restent définis
  // plus bas dans ce fichier mais ne sont simplement plus montés ici.
  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-muted-foreground">
        Politique de conservation légale des documents et préparation de l'e-reporting des ventes B2C. Porte sur
        la <strong>boutique de travail actuellement sélectionnée</strong>. Ces mécanismes fournissent des
        justificatifs techniques ; ils ne constituent ni une certification par un organisme accrédité, ni un avis
        juridique définitif.
      </p>
      <RetentionCard />
      <EreportingExportCard />
    </div>
  );
}

function formatBytes(n) {
  if (!n) return "0 o";
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} Mo`;
  return `${Math.round(n / 1024)} Ko`;
}

// Onglet « App » : téléchargement des applications SogoGestion (Windows / Android)
// et envoi (par morceaux) d'une nouvelle version par un administrateur.
const APP_CHUNK = 8 * 1024 * 1024;

function AppTab() {
  const [info, setInfo] = useState(null);
  const [progress, setProgress] = useState({}); // { windows: 0..100 }
  const fileRefs = useRef({});

  const load = () => api.get("/admin/app/info").then((r) => setInfo(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const download = (kind) => {
    window.location.href = `${api.defaults.baseURL}/admin/app/download/${kind}`;
  };

  const upload = async (kind, file) => {
    if (!file) return;
    const expected = kind === "windows" ? ".zip" : ".apk";
    if (!file.name.toLowerCase().endsWith(expected)) {
      toast.error(`Choisissez un fichier ${expected}`);
      return;
    }
    setProgress((p) => ({ ...p, [kind]: 0 }));
    try {
      for (let offset = 0; offset < file.size; offset += APP_CHUNK) {
        const blob = file.slice(offset, Math.min(offset + APP_CHUNK, file.size));
        await api.post(`/admin/app/upload/${kind}`, blob, {
          params: { offset, total: file.size },
          headers: { "Content-Type": "application/octet-stream" },
          transformRequest: [(d) => d],
        });
        setProgress((p) => ({ ...p, [kind]: Math.min(100, Math.round(((offset + blob.size) / file.size) * 100)) }));
      }
      toast.success("Fichier envoyé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'envoi");
    } finally {
      setProgress((p) => { const n = { ...p }; delete n[kind]; return n; });
      if (fileRefs.current[kind]) fileRefs.current[kind].value = "";
    }
  };

  const rows = [
    { kind: "windows", icon: Monitor, title: "Installer sur Windows", button: "Installer sur Windows",
      help: "Télécharge le dossier de l'application (.zip). Décompressez-le sur le poste Windows puis lancez SogoGestion.exe. Au premier lancement, saisissez l'adresse du site ; l'impression automatique des tickets et étiquettes se règle dans le menu de l'application." },
    { kind: "android", icon: Smartphone, title: "Installer l'APK Android", button: "Installer l'APK Android",
      help: "Télécharge l'application Android (.apk). Ouvrez-la sur le téléphone et autorisez l'installation depuis cette source. Au premier lancement, saisissez l'adresse du site." },
  ];

  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-app-tab">
      <p className="text-sm text-muted-foreground">
        Applications SogoGestion à installer sur les postes et téléphones. Elles ouvrent simplement le site (connexion obligatoire)
        et ajoutent l'impression automatique des tickets et des étiquettes.
      </p>
      {rows.map(({ kind, icon: Icon, title, button, help }) => {
        const i = info?.[kind];
        const pct = progress[kind];
        return (
          <div key={kind} className="rounded-xl border border-border bg-card p-5 space-y-3" data-testid={`app-card-${kind}`}>
            <div className="flex items-center gap-2 font-medium"><Icon size={18} /> {title}</div>
            <p className="text-sm text-muted-foreground">{help}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={() => download(kind)} disabled={!i?.available} className="gap-2" data-testid={`app-download-${kind}`}>
                <Download size={16} /> {button}
              </Button>
              {i?.available ? (
                <span className="text-xs text-muted-foreground" data-testid={`app-info-${kind}`}>
                  {i.filename} — {formatBytes(i.size)}{i.modified ? ` — ${new Date(i.modified).toLocaleDateString("fr-FR")}` : ""}
                </span>
              ) : (
                <span className="text-xs text-amber-600" data-testid={`app-missing-${kind}`}>Aucun fichier disponible : envoyez-le une première fois ci-dessous.</span>
              )}
            </div>
            {kind === "windows" && !i?.available && (
              <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
                <input
                  ref={(el) => { fileRefs.current[kind] = el; }}
                  type="file"
                  accept=".zip"
                  className="hidden"
                  data-testid={`app-file-${kind}`}
                  onChange={(e) => upload(kind, e.target.files?.[0])}
                />
                <Button variant="outline" size="sm" className="gap-2" disabled={pct !== undefined} onClick={() => fileRefs.current[kind]?.click()} data-testid={`app-upload-${kind}`}>
                  {pct !== undefined ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {pct !== undefined ? `Envoi... ${pct}%` : "Envoyer le fichier Windows (une seule fois)"}
                </Button>
                <span className="text-xs text-muted-foreground">Une fois envoyé, il ne pourra plus être remplacé.</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function AdminPage() {
  const [paEnabled, setPaEnabled] = useState(false);
  const [pendingDevices, setPendingDevices] = useState(0);
  const refreshPendingDevices = () => api.get("/devices/pending-count").then((r) => setPendingDevices(r.data.count || 0)).catch(() => {});
  useEffect(() => { api.get("/pa/config").then((r) => setPaEnabled(!!r.data.enabled)).catch(() => {}); refreshPendingDevices(); }, []);
  return (
    <Layout title="Administrateurs">
      <Tabs defaultValue="boutiques">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="boutiques" data-testid="admin-tab-boutiques" className="gap-1.5"><Store size={14} /> Boutiques & Dépôts</TabsTrigger>
          <TabsTrigger value="utilisateurs" data-testid="admin-tab-utilisateurs" className="gap-1.5"><Users size={14} /> Utilisateurs</TabsTrigger>
          {/* Numérotation retirée côté site (demande explicite) : gérée
              exclusivement depuis le panel d'hébergement désormais.
              NumberingTab reste défini plus bas dans ce fichier mais n'est
              simplement plus monté ici. */}
          <TabsTrigger value="appareils" data-testid="admin-tab-appareils" className="gap-1.5"><Monitor size={14} /> Appareils{pendingDevices > 0 && <span className="ml-1 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px]" data-testid="devices-pending-badge">{pendingDevices}</span>}</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-tab-categories" className="gap-1.5"><Tags size={14} /> Catégories</TabsTrigger>
          <TabsTrigger value="config-stock" data-testid="admin-tab-config-stock" className="gap-1.5"><Package size={14} /> Config Stock</TabsTrigger>
          <TabsTrigger value="marge" data-testid="admin-tab-marge" className="gap-1.5"><Percent size={14} /> Bénéfice par défaut</TabsTrigger>
          <TabsTrigger value="paiements" data-testid="admin-tab-paiements" className="gap-1.5"><Wallet size={14} /> Moyens de paiement</TabsTrigger>
          <TabsTrigger value="fidelite" data-testid="admin-tab-fidelite" className="gap-1.5"><Star size={14} /> Fidélité</TabsTrigger>
          <TabsTrigger value="securite" data-testid="admin-tab-securite" className="gap-1.5"><LockKeyhole size={14} /> Sécurité connexion</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit" className="gap-1.5"><ShieldCheck size={14} /> Audit de conformité</TabsTrigger>
          <TabsTrigger value="conformite-fiscale" data-testid="admin-tab-conformite-fiscale" className="gap-1.5"><FlaskConical size={14} /> Conformité fiscale</TabsTrigger>
          {/* Backup retiré côté site (demande explicite) : géré
              exclusivement depuis le panel d'hébergement désormais. */}
          {paEnabled && <TabsTrigger value="pa" data-testid="admin-tab-pa" className="gap-1.5"><FileBadge size={14} /> Facturation électronique</TabsTrigger>}
          <TabsTrigger value="logs" data-testid="admin-tab-logs" className="gap-1.5"><History size={14} /> Logs</TabsTrigger>
          <TabsTrigger value="app" data-testid="admin-tab-app" className="gap-1.5"><Smartphone size={14} /> App</TabsTrigger>
        </TabsList>
        <TabsContent value="boutiques"><ShopSettingsPage embedded /></TabsContent>
        <TabsContent value="utilisateurs"><UsersPage embedded /></TabsContent>
        <TabsContent value="appareils"><DevicesTab onChange={refreshPendingDevices} /></TabsContent>
        <TabsContent value="categories"><CategoriesTab /></TabsContent>
        <TabsContent value="config-stock"><ConfigStockTab /></TabsContent>
        <TabsContent value="marge"><MargeTab /></TabsContent>
        <TabsContent value="paiements"><PaymentMethodsTab /></TabsContent>
        <TabsContent value="fidelite"><FideliteTab /></TabsContent>
        <TabsContent value="securite"><SecuriteTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="conformite-fiscale"><ComplianceFiscaleTab /></TabsContent>
        {paEnabled && <TabsContent value="pa"><PaTab /></TabsContent>}
        <TabsContent value="logs"><LogsTab /></TabsContent>
        <TabsContent value="app"><AppTab /></TabsContent>
      </Tabs>
    </Layout>
  );
}
